import { FC, useState } from "react";
import { Modal } from "flowbite-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { signOut, useSession } from "next-auth/react";
import {
  FaBars,
  FaKey,
  FaShoppingCart,
  FaTicketAlt,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { CogIcon } from "@heroicons/react/24/solid";
import { KeyIcon } from "@heroicons/react/24/outline";
import { RiCoupon3Fill } from "react-icons/ri";
import utils from "@/shared/utils";
import RoleGuard from "@/shared/RoleGuard";

type Props = {
  qtdCart: any;
};

const NavbarSite: FC<Props> = ({ qtdCart }: Props) => {
  const router = useRouter();
  const { data: session } = useSession();
  const [menuMobile, setMenuMobile] = useState(false);
  const [modalConta, setModalConta] = useState(false);

  const abrirAdmin = () => {
    router.push("/dashboard");
  };

  const sair = () => {
    utils.deleleCartCookie();
    utils.deleleQtdCart();
    signOut();
  };

  const itensConta = [
    {
      label: "Meus Ingressos",
      href: "/dashboard/pedidos",
      icone: <FaTicketAlt className="h-4 w-4" />,
      mostrar: true,
    },
    {
      label: "Carrinho",
      href: "/checkout/cart",
      icone: <FaShoppingCart className="h-4 w-4" />,
      mostrar: true,
    },
    {
      label: "Meu Perfil",
      href: "/dashboard/perfil",
      icone: <FaUser className="h-4 w-4" />,
      mostrar: true,
    },
    {
      label: "Alterar Senha",
      href: "/dashboard/perfil/senha",
      icone: <FaKey className="h-4 w-4" />,
      mostrar: true,
    },
    {
      label: "Meus Cupons",
      href: "/dashboard/cupons",
      icone: <RiCoupon3Fill className="h-4 w-4" />,
      mostrar: RoleGuard.canRole("influencer", session?.user.roles ?? []),
    },
    {
      label: "Administrativo",
      href: "/admin",
      icone: <KeyIcon className="h-4 w-4" />,
      mostrar: RoleGuard.canRole("administrador", session?.user.roles),
    },
    {
      label: "Gerenciador",
      href: "/gerenciador",
      icone: <CogIcon className="h-4 w-4" />,
      mostrar: (session?.user.empresas ?? 0) > 0,
    },
  ].filter((item) => item.mostrar);

  const listaConta = () => (
    <div className="space-y-2">
      {itensConta.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          onClick={() => {
            setModalConta(false);
            setMenuMobile(false);
          }}
          className="flex items-center justify-center gap-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-semibold uppercase text-gray-900 hover:bg-gray-50"
        >
          {item.icone}
          {item.label}
        </Link>
      ))}
      <button
        type="button"
        onClick={sair}
        className="w-full rounded-lg bg-red-600 hover:bg-red-700 px-4 py-3 text-sm font-semibold uppercase text-white"
      >
        Sair
      </button>
    </div>
  );

  const botoesVisitante = () => (
    <>
      <button
        type="button"
        onClick={abrirAdmin}
        className="px-4 py-2 border-2 border-red-600 hover:border-red-700 rounded-lg text-sm font-semibold text-red-600 hover:text-red-700"
      >
        ENTRAR
      </button>
      <button
        type="button"
        onClick={() => router.push("/auth/register")}
        className="px-4 py-2 border-2 bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700 rounded-lg text-sm font-semibold text-white"
      >
        CADASTRAR
      </button>
    </>
  );

  return (
    <header className="sticky top-0 w-full bg-white shadow-sm" style={{ zIndex: 40 }}>
      <div className="container mx-auto flex items-center justify-between gap-4 px-4 md:px-10 py-2">
        <Link href="/" aria-label="Página inicial" className="shrink-0">
          <Image
            src="/images/logo-vermelha.png"
            alt="Agência Mix"
            width={100}
            height={54}
            priority
          />
        </Link>

        <div className="flex items-center gap-3">
          <Link
            href="/checkout/cart"
            aria-label={`Carrinho com ${qtdCart ?? 0} item(ns)`}
            className="relative p-2"
          >
            <FaShoppingCart className="text-myred text-2xl" />
            {Number(qtdCart) > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-xs font-semibold">
                {qtdCart}
              </span>
            )}
          </Link>

          <div className="hidden md:flex items-center gap-3">
            {!session && botoesVisitante()}
            {session && (
              <button
                type="button"
                onClick={() => setModalConta(true)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-red-600 rounded-lg text-sm font-semibold text-red-600 hover:text-red-700 hover:border-red-700"
              >
                <FaUser />
                Olá, {utils.primeiroNome(session?.user?.name)}
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMenuMobile(!menuMobile)}
            aria-label="Abrir menu"
            aria-expanded={menuMobile}
            className="md:hidden p-2 text-2xl text-gray-900"
          >
            {menuMobile ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {menuMobile && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-4 shadow-lg">
          {session ? (
            <>
              <p className="text-center font-semibold text-gray-900 mb-3">
                Olá, {utils.primeiroNome(session?.user?.name)}
              </p>
              {listaConta()}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">{botoesVisitante()}</div>
          )}
        </div>
      )}

      <Modal show={modalConta} size="md" popup={false} onClose={() => setModalConta(false)}>
        <Modal.Header>Minha Conta</Modal.Header>
        <Modal.Body>{listaConta()}</Modal.Body>
      </Modal>
    </header>
  );
};

export default NavbarSite;
